/**
 * @puffin-core/auth-client — pre-aliased `createAuthContext` surfaces.
 *
 * AUTHSVC-CONSOLIDATION-002. `createAuthContext` calls `authService.verifyMfa(
 * mfaToken, code, method)` POSITIONALLY and expects it to THROW + store tokens
 * (the `verifyMfaForContext` projection). Every app used to hand-alias that
 * projection onto the object it feeds `createAuthContext` — and a single typo
 * (`verifyMfa: factory.verifyMfa`, the never-throws result-object twin) silently
 * "succeeds" an MFA failure with no tokens (the MFA-LOGIN-CONSOLIDATION-001 bug
 * class). These two builders do the alias mapping ONCE, here, so apps spread a
 * factory-provided object instead of hand-aliasing the catastrophic-silent keys.
 *
 * The raw projections (`verifyMfa`/`sendMfaOtp`/`authenticateWithPasskey`) STAY
 * on the top-level service object for no-AuthProvider consumers (marketing-apex
 * login, device flows) — the dual projection is preserved exactly. These
 * builders only re-bind already-built methods; they add no I/O (transport-
 * agnostic factory invariant holds).
 *
 * @module @puffin-core/auth-client/operations/contextSurface
 */

/**
 * The tenant-shaped pre-aliased object an app spreads into the object it feeds
 * `createAuthContext` (and its per-app passkey overlay). Carries ONLY the
 * context projections — the catastrophic-silent
 * `{verifyMfa, sendMfaOtp, authenticateWithPasskey}` trio plus the uniform
 * single-projection context methods (`exchangeCrossAppToken` and the tenant
 * login/logout/validate legs). The `*Tenant` aliases and the platform-leg
 * overlay the retired DUAL `createAuthContext` mode consumed are gone
 * (Q-L16-98, 2026-09-29): a tenant context is only ever tenant. It
 * deliberately omits plain getters (`getCurrentUser`/`getLocations`) so a
 * divergent app (pos-web, patrol-web) can preserve its `getLocations` omission
 * — `createAuthContext` guards location restore with `&& authService.getLocations`.
 *
 * @param {Object} service - the assembled createAuthService superset object
 * @returns {Object} pre-aliased tenant context surface
 */
export function buildTenantContextSurface(service) {
  return {
    login: service.loginForContext,
    logout: service.logoutTenant,
    validateToken: service.validateTenantToken,
    verifyMfa: service.verifyMfaForContext,
    // ECOSYSTEM-EMAIL-001 Q-EE-34 P5: the passkey second factor — its challenge, and the session-INSTALLING verify.
    getMfaPasskeyOptions: service.getMfaPasskeyOptionsForContext,
    verifyMfaPasskey: service.verifyMfaPasskeyForContext,
    sendMfaOtp: service.sendMfaOtpForContext,
    authenticateWithPasskey: service.authenticateWithPasskeyForContext,
    exchangeCrossAppToken: service.exchangeCrossAppToken,
    // Q-UMB-9 unit 3c: session-INSTALLING too — the SSO redirect's fragment bootstrap.
    resumeSession: service.resumeSession,
    // AUTHENTICATOR-CHALLENGE-FANOUT (2026-08-29). Carried HERE rather than added to nine app
    // `authService.js` files for the same reason the trio above is: it is a session-INSTALLING
    // method, so an app that forgot it would show a login page that spins forever on an approved
    // push, and an app that mis-aliased it would install a session with no tokens. Because every
    // app already spreads `...factory.contextSurface`, this reaches all eight shared-context apps
    // with zero per-app service edits.
    completeAuthenticatorChallenge: service.completeAuthenticatorChallenge,
    // Owner ruling Q-ARG-1 ("Required again", 2026-10-08): the tenant ENROLMENT leg a sign-in's MFA_SETUP_REQUIRED
    // refusal opens. `completeMfaEnrollment` installs the session the server mints, so it is carried here — once —
    // for the same reason the session installers above are.
    beginMfaEnrollment: service.beginMfaEnrollmentForContext,
    completeMfaEnrollment: service.completeMfaEnrollmentForContext,
    // SECREQ S6b: the passkey enrolment a passkey hold opens; `completePasskeyEnrollment` installs the session, so it is
    // carried here once, for the same reason.
    beginPasskeyEnrollment: service.beginPasskeyEnrollmentForContext,
    completePasskeyEnrollment: service.completePasskeyEnrollmentForContext,
  };
}
